/**
 * Packages & Services builder — service create/edit form. Extracted
 * verbatim from the former AgencyServicesPage monolith (Task 150-A).
 * No behavior change: same `upsertAgencyService` payload, same preset
 * auto-fill, same capability toggling, same validation.
 */

import { useState, type FormEvent } from 'react';
import { upsertAgencyService } from '../../api/endpoints';
import type {
  AgencyServiceRow,
  ServiceBillingInterval,
  SystemCapabilityKey,
} from '../../api/types';
import { capabilityLabelAr, SYSTEM_CAPABILITY_KEYS } from '../../permissions';
import { SERVICE_BILLING_INTERVALS, SERVICE_PRESETS } from './constants';
import { FormPanel, SimpleField, SimpleSelect } from './formPrimitives';
import { handleApiErr } from './helpers';
import { isCommercialCapabilityVisible } from './launchVisibility';

const VISIBLE_SYSTEM_CAPABILITY_KEYS = SYSTEM_CAPABILITY_KEYS.filter(isCommercialCapabilityVisible);

export function ServiceForm({
  mode,
  initial,
  onClose,
  onSaved,
}: {
  readonly mode: 'create' | 'edit';
  readonly initial: AgencyServiceRow | null;
  readonly onClose: () => void;
  readonly onSaved: () => void;
}) {
  const [presetId, setPresetId] = useState<string | null>(null);
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [category, setCategory] = useState(initial?.category ?? '');
  const [primaryCapability, setPrimaryCapability] = useState<SystemCapabilityKey>(
    initial?.system_capability_key ?? 'content',
  );
  const [capabilities, setCapabilities] = useState<ReadonlyArray<SystemCapabilityKey>>(
    initial?.capability_keys ?? [initial?.system_capability_key ?? 'content'],
  );
  const [priceAmount, setPriceAmount] = useState(
    initial?.default_price_amount !== null && initial?.default_price_amount !== undefined
      ? String(initial.default_price_amount)
      : '',
  );
  const [priceCurrency, setPriceCurrency] = useState(initial?.default_price_currency ?? 'SAR');
  const [billingInterval, setBillingInterval] = useState<ServiceBillingInterval | ''>(
    initial?.billing_interval ?? '',
  );
  const [isActive, setIsActive] = useState(initial?.is_active ?? true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function applyPreset(presetId: string) {
    setPresetId(presetId);
    const preset = SERVICE_PRESETS.find((p) => p.id === presetId);
    if (!preset) return;
    if (preset.id === 'custom') {
      // custom — leave fields untouched, user fills them in.
      return;
    }
    if (mode === 'create') {
      // Only auto-fill on create, never overwrite an existing service.
      if (name.trim().length === 0) setName(preset.name);
      if (description.trim().length === 0) setDescription(preset.suggestedDescription);
      setPrimaryCapability(preset.suggestedPrimary);
      setCapabilities(preset.suggestedCapabilities);
    }
  }

  function toggleCapability(key: SystemCapabilityKey) {
    if (key === primaryCapability) return; // primary is locked-in
    setCapabilities((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    const trimmed = name.trim();
    if (trimmed.length === 0) {
      setError('اسم الخدمة مطلوب.');
      return;
    }
    let parsedAmount: number | null = null;
    if (priceAmount.trim().length > 0) {
      const n = Number(priceAmount);
      if (!Number.isFinite(n) || n < 0) {
        setError('السعر يجب أن يكون رقماً غير سالب.');
        return;
      }
      parsedAmount = n;
    }
    // Ensure primary is in the capability set.
    const finalCapabilities = Array.from(new Set([primaryCapability, ...capabilities]));

    setSubmitting(true);
    try {
      await upsertAgencyService({
        input: {
          ...(initial?.id ? { id: initial.id } : {}),
          name: trimmed,
          description: description.trim() || null,
          category: category.trim() || null,
          system_capability_key: primaryCapability,
          capability_keys: finalCapabilities,
          default_price_amount: parsedAmount,
          default_price_currency: priceCurrency.toUpperCase(),
          billing_interval: billingInterval === '' ? null : billingInterval,
          is_active: isActive,
        },
      });
      onSaved();
    } catch (err) {
      setSubmitting(false);
      handleApiErr(err, setError);
    }
  }

  return (
    <FormPanel title={mode === 'create' ? 'خدمة جديدة' : `تعديل: ${initial?.name ?? ''}`}>
      <form onSubmit={onSubmit} noValidate>
        {error ? (
          <div className="banner banner-error" style={{ marginBottom: 12 }}>
            {error}
          </div>
        ) : null}

        {mode === 'create' ? (
          <div className="auth-field" style={{ marginBottom: 14 }}>
            <label className="auth-field-label">اختر نوع الخدمة (اختياري)</label>
            <p className="tiny" style={{ color: 'var(--muted)', marginBottom: 8 }}>
              اختر أحد الأنواع الجاهزة لتعبئة الحقول تلقائياً، أو "خدمة مخصصة" لإدخال البيانات
              يدوياً.
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {SERVICE_PRESETS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className={presetId === p.id ? 'chip on' : 'chip'}
                  onClick={() => applyPreset(p.id)}
                  disabled={submitting}
                >
                  {p.name}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        <SimpleField
          label="اسم الخدمة"
          required
          value={name}
          onChange={setName}
          maxLength={160}
          placeholder="مثال: إدارة السوشال ميديا والمحتوى"
        />

        <SimpleField
          label="الوصف (اختياري)"
          value={description}
          onChange={setDescription}
          textarea
          maxLength={2000}
        />

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))',
            gap: 14,
            marginTop: 12,
          }}
        >
          <SimpleField
            label="الفئة (اختياري)"
            value={category}
            onChange={setCategory}
            maxLength={80}
            placeholder="مثال: إدارة الحسابات"
          />
          <SimpleField
            label="السعر الافتراضي (اختياري)"
            value={priceAmount}
            onChange={setPriceAmount}
            type="number"
            placeholder="0"
            dirLtr
          />
          <SimpleField
            label="العملة"
            value={priceCurrency}
            onChange={setPriceCurrency}
            maxLength={3}
            dirLtr
          />
          <SimpleSelect
            label="فترة الفوترة"
            value={billingInterval}
            onChange={(v) => setBillingInterval(v as ServiceBillingInterval | '')}
            options={SERVICE_BILLING_INTERVALS.map((i) => ({ value: i.value, label: i.label }))}
          />
        </div>

        <div className="auth-field" style={{ marginTop: 12 }}>
          <label className="auth-field-label">القدرة الأساسية (★)</label>
          <p className="tiny" style={{ color: 'var(--muted)', marginBottom: 8 }}>
            القدرة الأساسية التي تُمثّل هذه الخدمة داخل النظام.
          </p>
          <SimpleSelect
            label=""
            value={primaryCapability}
            onChange={(v) => {
              const key = v as SystemCapabilityKey;
              setPrimaryCapability(key);
              setCapabilities((prev) => Array.from(new Set([key, ...prev])));
            }}
            options={VISIBLE_SYSTEM_CAPABILITY_KEYS.map((k) => ({
              value: k,
              label: capabilityLabelAr(k),
            }))}
          />
        </div>

        <div className="auth-field" style={{ marginTop: 12 }}>
          <label className="auth-field-label">ما الذي تفعّله هذه الخدمة داخل النظام</label>
          <p className="tiny" style={{ color: 'var(--muted)', marginBottom: 8 }}>
            اختر القدرات الإضافية التي تُتيحها الخدمة. القدرة الأساسية مُختارة تلقائياً.
          </p>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {VISIBLE_SYSTEM_CAPABILITY_KEYS.map((key) => {
              const isPrimary = key === primaryCapability;
              const isOn = capabilities.includes(key);
              return (
                <button
                  key={key}
                  type="button"
                  className={isOn ? 'chip on' : 'chip'}
                  onClick={() => toggleCapability(key)}
                  disabled={isPrimary || submitting}
                  style={isPrimary ? { opacity: 1 } : undefined}
                  title={isPrimary ? 'القدرة الأساسية لا تُحذف' : undefined}
                >
                  {capabilityLabelAr(key)}
                  {isPrimary ? ' ★' : ''}
                </button>
              );
            })}
          </div>
        </div>

        <label
          style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 13 }}
        >
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.currentTarget.checked)}
            disabled={submitting}
          />
          <span>الخدمة نشطة</span>
        </label>

        <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
          <button
            type="submit"
            className="auth-cta"
            disabled={submitting}
            style={{ width: 'auto' }}
          >
            {submitting ? <span className="spinner" /> : null}
            <span>{submitting ? 'جاري الحفظ…' : 'حفظ'}</span>
          </button>
          <button
            type="button"
            className="btn-text"
            onClick={onClose}
            disabled={submitting}
            style={{ padding: '10px 14px' }}
          >
            إلغاء
          </button>
        </div>
      </form>
    </FormPanel>
  );
}
